import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Mail, TrendingUp, ChevronRight, ArrowUpRight, ArrowDownRight, KeyRound, Copy, ShieldCheck, ShieldOff, Link2, FileText, ClipboardList, BookMarked, Eye, Trash2, ClipboardCheck } from "lucide-react";
import { API } from "@/lib/api";

const scoreColor = (s) => (s >= 70 ? "#00A859" : s >= 40 ? "#F59E0B" : "#FF2A2A");

const STAGE_BG = { lead: "bg-blue-100 text-blue-800", proposta: "bg-amber-100 text-amber-800", vinto: "bg-emerald-100 text-emerald-800", perso: "bg-gray-200 text-gray-600" };
const STAGE_LBL = { lead: "Lead", proposta: "Proposta", vinto: "Vinto", perso: "Perso" };

function StageTimeline({ clientId }) {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    api.get(`/pipeline/${clientId}/stage-history`).then((r) => setHist(r.data)).catch(() => {});
  }, [clientId]);
  if (!hist) return null;
  const fmtD = (iso) => new Date(iso).toLocaleDateString("it-IT");
  const days = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid="stage-timeline">
      <div className="data-label mb-2">Trattativa</div>
      <span className={`text-[10px] font-bold px-2 py-0.5 ${STAGE_BG[hist.current_stage] || STAGE_BG.lead}`} data-testid="current-stage-badge">
        {STAGE_LBL[hist.current_stage] || hist.current_stage}
      </span>
      {hist.events.length > 0 ? (
        <div className="mt-3 space-y-1.5">
          {hist.events.map((e, i) => (
            <div key={e.id} className="text-xs flex items-center justify-between gap-2" data-testid={`stage-event-${i}`}>
              <span className="text-muted-foreground">
                {STAGE_LBL[e.from_stage]} → <span className="font-medium text-foreground">{STAGE_LBL[e.to_stage]}</span>
              </span>
              <span className="font-mono text-muted-foreground whitespace-nowrap">
                {fmtD(e.changed_at)}
                {" · +"}
                {i === 0 ? days(hist.created_at || e.changed_at, e.changed_at) : days(hist.events[i - 1].changed_at, e.changed_at)}gg
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mt-2">Nessun cambio di stato registrato.</p>
      )}
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useAuth();
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [propStatus, setPropStatus] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/clients/${id}`);
    setData(data);
    api.get(`/clients/${id}/proposal-status`).then((r) => setPropStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  const newAssessment = async () => {
    setCreating(true);
    try {
      const { data: a } = await api.post(`/clients/${id}/assessments`);
      toast.success("Nuovo assessment creato");
      navigate(`/readiness/${a.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setCreating(false); }
  };

  if (!data) return <Layout><PageHeader title="Cliente" /><div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div></Layout>;

  const c = data.client;
  const ev = data.evolution || [];
  const chart = ev.map((e) => ({ d: new Date(e.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" }), v: e.overall }));

  return (
    <Layout>
      <PageHeader title={c.name} subtitle={c.sector || "Cliente"}>
        <Button variant="outline" className="rounded-none" onClick={() => navigate("/clients")}><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
        <Button variant="outline" className="rounded-none" data-testid="download-report-btn" onClick={() => window.open(`${API}/clients/${id}/report.pdf`, "_blank")}><FileText className="w-4 h-4 mr-2" /> Report PDF</Button>
        <Button variant="outline" className="rounded-none" data-testid="download-proposal-btn" onClick={() => window.open(`${API}/clients/${id}/proposal.pdf`, "_blank")}><ClipboardCheck className="w-4 h-4 mr-2" /> Proposta PDF</Button>
        {propStatus?.accepted ? (
          <span data-testid="proposal-accepted-badge" title={`Firmata da ${propStatus.acceptance?.signer_name || ""}`} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1.5 bg-emerald-100 text-emerald-800">
            <ClipboardCheck className="w-3.5 h-3.5" /> {propStatus.acceptance?.paid ? "Proposta pagata" : "Proposta accettata"}
          </span>
        ) : (
          <span data-testid="proposal-pending-badge" className="inline-flex items-center text-xs font-medium px-2 py-1.5 bg-secondary text-muted-foreground">Proposta in attesa</span>
        )}
        {canEdit && <Button className="rounded-none" data-testid="new-assessment-btn" onClick={newAssessment} disabled={creating}>{creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Nuovo assessment</Button>}
      </PageHeader>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-border p-6">
          <div className="data-label mb-3">Referenti</div>
          <div className="space-y-3">
            {(c.contacts || []).map((ct, i) => (
              <div key={i} className="border-b border-border pb-2 last:border-0" data-testid={`contact-${i}`}>
                <div className="font-medium text-sm">{ct.name} {ct.role && <span className="text-muted-foreground font-normal">· {ct.role}</span>}</div>
                {ct.email && <a href={`mailto:${ct.email}`} className="text-xs text-primary hover:underline flex items-center gap-1"><Mail className="w-3 h-3" />{ct.email}</a>}
                {ct.phone && <div className="text-xs text-muted-foreground">{ct.phone}</div>}
              </div>
            ))}
            {(c.contacts || []).length === 0 && <p className="text-sm text-muted-foreground">Nessun referente.</p>}
          </div>
          {c.notes && <div className="mt-4 pt-4 border-t border-border"><div className="data-label mb-1">Note</div><p className="text-sm text-muted-foreground">{c.notes}</p></div>}
          <StageTimeline clientId={id} />
        </div>

        <div className="lg:col-span-2 bg-white border border-border p-6" data-testid="evolution-chart">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-primary" /><h3 className="font-heading font-bold text-lg tracking-tight">Evoluzione punteggio</h3></div>
          {chart.length >= 2 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="d" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#9CA3AF" />
                <RTooltip contentStyle={{ borderRadius: 0, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Line type="monotone" dataKey="v" stroke="#002FA7" strokeWidth={2} dot={{ r: 4, fill: "#002FA7" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">Servono almeno 2 assessment per vedere il trend di miglioramento. Crea un nuovo assessment per questo cliente.</p>
          )}
        </div>

        <div className="lg:col-span-3 bg-white border border-border" data-testid="client-assessments">
          <div className="p-4 border-b border-border data-label">Assessment nel tempo ({data.assessments.length})</div>
          {data.assessments.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6">Nessun assessment. Clicca "Nuovo assessment" per iniziare.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left">
                <th className="data-label px-4 py-3">Data</th>
                <th className="data-label px-4 py-3">Punteggio</th>
                <th className="data-label px-4 py-3">Maturità</th>
                <th className="data-label px-4 py-3">Δ vs precedente</th>
                <th className="data-label px-4 py-3">Stato</th>
                <th className="data-label px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {ev.map((e, i) => {
                  const row = data.assessments.find((x) => x.id === e.assessment_id);
                  return (
                    <tr key={e.assessment_id} data-testid={`assessment-row-${i}`} onClick={() => navigate(`/readiness/${e.assessment_id}`)} className={`border-b border-border cursor-pointer hover:bg-secondary ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}>
                      <td className="px-4 py-3">{new Date(e.date).toLocaleDateString("it-IT")}</td>
                      <td className="px-4 py-3 font-mono font-semibold" style={{ color: scoreColor(e.overall) }}>{e.overall}</td>
                      <td className="px-4 py-3">{row?.tier && <span className="text-[10px] font-bold text-white px-2 py-0.5" style={{ backgroundColor: row.tier.color }}>{row.tier.label}</span>}</td>
                      <td className="px-4 py-3 font-mono">
                        {e.delta == null ? <span className="text-muted-foreground">—</span> : (
                          <span className={e.delta >= 0 ? "text-[#00A859] flex items-center gap-1" : "text-destructive flex items-center gap-1"}>
                            {e.delta >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}{e.delta >= 0 ? "+" : ""}{e.delta}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{row?.has_deliverable ? <span className="text-[10px] font-bold bg-primary text-primary-foreground px-2 py-0.5">DELIVERABLE</span> : <span className="data-label">bozza</span>}{row?.self_assessed && <span className="text-[10px] font-bold bg-secondary px-2 py-0.5 ml-1">AUTO</span>}</td>
                      <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {canEdit && <div className="px-8 pb-8"><PortalAccessCard clientId={id} /></div>}
      <div className="px-8 pb-8"><SurveyHistory clientId={id} canEdit={canEdit} /></div>
    </Layout>
  );
}

const priorityBadge = { ALTA: "bg-destructive/10 text-destructive", MEDIA: "bg-[#F59E0B]/10 text-[#B45309]", BASSA: "bg-[#00A859]/10 text-[#00784A]" };
const complLevelColor = (s) => (s >= 80 ? "#00A859" : s >= 50 ? "#F59E0B" : "#DC2626");

function SurveyHistory({ clientId, canEdit }) {
  const [surveys, setSurveys] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");

  const load = async () => {
    const { data } = await api.get(`/clients/${clientId}/surveys`);
    setSurveys(data);
    if (data.length >= 2) {
      setCmpA(data[data.length - 1].id); // più vecchio
      setCmpB(data[0].id);               // più recente
    }
  };
  useEffect(() => { load(); }, [clientId]);

  const remove = async (sid) => {
    if (!window.confirm("Eliminare questo sopralluogo dallo storico?")) return;
    await api.delete(`/clients/${clientId}/surveys/${sid}`);
    toast.success("Sopralluogo eliminato");
    if (openId === sid) setOpenId(null);
    load();
  };

  if (!surveys) return null;

  const FW = ["NIS2", "DORA", "GDPR", "OT"];
  const sa = surveys.find((s) => s.id === cmpA);
  const sb = surveys.find((s) => s.id === cmpB);
  const dLabel = (s) => s ? new Date(s.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" }) + " " + new Date(s.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div className="bg-white border border-border" data-testid="survey-history">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-bold text-lg tracking-tight">Storico sopralluoghi</h3>
        <span className="data-label ml-auto">{surveys.length}</span>
      </div>

      {surveys.length >= 2 && sa && sb && (
        <div className="p-4 border-b border-border bg-primary/5" data-testid="survey-compare">
          <div className="flex items-center gap-2 data-label text-primary mb-3"><TrendingUp className="w-3.5 h-3.5" /> Confronto conformità nel tempo</div>
          <div className="grid grid-cols-2 gap-3 mb-3 max-w-md">
            <div>
              <label className="data-label">Da</label>
              <select data-testid="cmp-a-select" className="mt-1 w-full h-9 border border-border rounded-none text-sm px-2 bg-white" value={cmpA} onChange={(e) => setCmpA(e.target.value)}>
                {surveys.map((s) => <option key={s.id} value={s.id}>{dLabel(s)}</option>)}
              </select>
            </div>
            <div>
              <label className="data-label">A</label>
              <select data-testid="cmp-b-select" className="mt-1 w-full h-9 border border-border rounded-none text-sm px-2 bg-white" value={cmpB} onChange={(e) => setCmpB(e.target.value)}>
                {surveys.map((s) => <option key={s.id} value={s.id}>{dLabel(s)}</option>)}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto border border-border bg-white">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-border">
                <th className="data-label px-3 py-2">Framework</th>
                <th className="data-label px-3 py-2 text-right">{dLabel(sa)}</th>
                <th className="data-label px-3 py-2 text-right">{dLabel(sb)}</th>
                <th className="data-label px-3 py-2 text-right">Δ</th>
              </tr></thead>
              <tbody>
                {[...FW, "__overall"].map((fw) => {
                  const isOverall = fw === "__overall";
                  const va = isOverall ? sa.compliance?.overall?.score : sa.compliance?.scores?.[fw]?.score;
                  const vb = isOverall ? sb.compliance?.overall?.score : sb.compliance?.scores?.[fw]?.score;
                  if (va == null && vb == null) return null;
                  const delta = (vb || 0) - (va || 0);
                  return (
                    <tr key={fw} data-testid={`cmp-row-${isOverall ? "overall" : fw}`} className={`border-b border-border ${isOverall ? "font-semibold bg-secondary/40" : ""}`}>
                      <td className="px-3 py-2">{isOverall ? "Conformità complessiva" : fw}</td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: complLevelColor(va || 0) }}>{va != null ? `${va}%` : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: complLevelColor(vb || 0) }}>{vb != null ? `${vb}%` : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {delta === 0 ? <span className="text-muted-foreground">0</span> : (
                          <span className={delta > 0 ? "text-[#00A859] inline-flex items-center gap-0.5" : "text-destructive inline-flex items-center gap-0.5"}>
                            {delta > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}{delta > 0 ? "+" : ""}{delta}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Interventi pianificati: {sa.compliance?.planning?.length ?? 0} → {sb.compliance?.planning?.length ?? 0}</p>
        </div>
      )}

      {surveys.length === 0 ? (
        <p className="text-sm text-muted-foreground p-6">Nessun sopralluogo salvato. Completa il Wizard "Sopralluogo" per creare uno snapshot storicizzato.</p>
      ) : (
        <div className="divide-y divide-border">
          {surveys.map((s) => {
            const open = openId === s.id;
            const overall = s.compliance?.overall?.score;
            const planning = s.compliance?.planning || [];
            return (
              <div key={s.id} data-testid={`survey-${s.id}`}>
                <div className="flex items-center gap-4 px-4 py-3 hover:bg-secondary/40 cursor-pointer" onClick={() => setOpenId(open ? null : s.id)}>
                  <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{new Date(s.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })} — {new Date(s.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="text-xs text-muted-foreground">{s.tools_count || 0} tool · {planning.length} interventi pianificati{s.readiness_score != null ? ` · readiness ${s.readiness_score}/100` : ""}</div>
                  </div>
                  {overall != null && <span className="font-mono text-sm font-semibold" style={{ color: complLevelColor(overall) }}>{overall}%</span>}
                  {canEdit && <button onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="text-muted-foreground hover:text-destructive" data-testid={`survey-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
                {open && (
                  <div className="px-4 pb-4 bg-[#F9FAFB]" data-testid={`survey-detail-${s.id}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 py-3">
                      {Object.entries(s.compliance?.scores || {}).map(([fw, sc]) => (
                        <div key={fw} className="border border-border bg-white p-2 text-center">
                          <div className="data-label">{fw}</div>
                          <div className="font-mono font-semibold" style={{ color: complLevelColor(sc.score) }}>{sc.score}%</div>
                        </div>
                      ))}
                    </div>
                    {planning.length > 0 ? (
                      <div className="overflow-x-auto border border-border bg-white">
                        <table className="w-full text-sm">
                          <thead><tr className="text-left border-b border-border">
                            <th className="data-label px-3 py-2">Issue</th>
                            <th className="data-label px-3 py-2">Prio</th>
                            <th className="data-label px-3 py-2">Improvement</th>
                            <th className="data-label px-3 py-2">Norma</th>
                          </tr></thead>
                          <tbody>
                            {planning.map((r, i) => (
                              <tr key={i} className={`border-b border-border align-top ${i % 2 ? "bg-[#F9FAFB]" : "bg-white"}`}>
                                <td className="px-3 py-2"><span className="text-[10px] font-mono bg-primary/10 text-primary px-1.5 py-0.5 mr-1">{r.framework}</span>{r.issue} <span className="text-xs text-muted-foreground">({r.status})</span></td>
                                <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold ${priorityBadge[r.priority] || "bg-secondary"}`}>{r.priority}</span></td>
                                <td className="px-3 py-2 text-muted-foreground">{r.improvement}{r.tools?.length ? <div className="text-xs mt-1">Tool: {r.tools.join(", ")}</div> : null}</td>
                                <td className="px-3 py-2 text-xs text-primary/80">{r.regulation_ref || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : <p className="text-sm text-muted-foreground py-2">Nessun gap di conformità in questo sopralluogo.</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PortalAccessCard({ clientId }) {
  const [state, setState] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const portalUrl = `${window.location.origin}/portal`;

  const load = async () => {
    const { data } = await api.get(`/clients/${clientId}/portal-access`);
    setState(data);
    if (data.exists) setEmail(data.email);
  };
  useEffect(() => { load(); }, [clientId]);

  const save = async () => {
    if (!email.trim()) { toast.error("Email obbligatoria"); return; }
    if (!state?.exists && !password) { toast.error("Imposta una password iniziale"); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/portal-access`, { email, password: password || undefined });
      toast.success("Accesso portale salvato");
      setPassword("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revocare l'accesso al portale per questo cliente?")) return;
    await api.delete(`/clients/${clientId}/portal-access`);
    toast.success("Accesso revocato"); setPassword(""); load();
  };

  if (!state) return null;

  return (
    <div className="bg-white border border-border p-6" data-testid="portal-access-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 data-label"><KeyRound className="w-3.5 h-3.5 text-primary" /> Accesso Portale Cliente</div>
        {state.exists && (
          <span className={`text-[10px] font-bold px-2 py-0.5 text-white ${state.active ? "bg-[#00A859]" : "bg-muted-foreground"}`}>
            {state.active ? "ATTIVO" : "DISATTIVO"}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Crea credenziali con cui il cliente accede in sola lettura al portale (KPI ed evoluzione del punteggio).
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="data-label">Email cliente</Label>
          <Input data-testid="portal-access-email" type="email" className="mt-1 rounded-none" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@azienda.it" />
        </div>
        <div>
          <Label className="data-label">{state.exists ? "Nuova password (opzionale)" : "Password iniziale"}</Label>
          <Input data-testid="portal-access-password" type="text" className="mt-1 rounded-none font-mono" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={state.exists ? "Lascia vuoto per non cambiare" : "Imposta password"} />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 flex-wrap">
        <Button className="rounded-none" onClick={save} disabled={busy} data-testid="portal-access-save">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          {state.exists ? "Aggiorna accesso" : "Crea accesso"}
        </Button>
        {state.exists && (
          <>
            <Button variant="outline" className="rounded-none" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Link portale copiato"); }} data-testid="portal-access-copy-link">
              <Link2 className="w-4 h-4 mr-2" /> Copia link portale
            </Button>
            <Button variant="outline" className="rounded-none text-destructive hover:text-destructive" onClick={revoke} data-testid="portal-access-revoke">
              <ShieldOff className="w-4 h-4 mr-2" /> Revoca
            </Button>
          </>
        )}
      </div>
      {state.exists && <p className="text-xs text-muted-foreground mt-3 font-mono">{portalUrl}</p>}
    </div>
  );
}
